import _ from "underscore";
import analytics from "web/script/analytics/analytics";
import baustein from "baustein";
import browser from "web/script/utils/browser";
import form from "web/script/dom/forms";
import traversal from "web/script/dom/traversal";
import rothko from "web/script/modules/rothko";
import { addVisibilityWatcher } from "web/script/modules/run-loop";
import url from "web/script/utils/url";
import style from "web/script/dom/style";
import history from "web/script/utils/history";
import template from "templates/modules/universal_filters/universal_filters.jinja";
import store from "web/redux/store";
import { watchState } from "web/redux/state-watcher";
import { clearFilters, setActiveFilters, clearPendingFilters } from "web/redux/ducks/feed-filters";
import { closeSidebar } from "web/redux/ducks/feed-sidebar";
import { hideSeoContent } from "web/redux/ducks/feed-seo-content";

const CLEAR_BUTTON_SELECTOR = "a[data-clear-name]";
const MOBILE_SECTION_BUTTON_SELECTOR =
    ".universal-filters__filter-section-accordion-title--flat-button";
const RADIO_BUTTONS_SELECTOR = 'input[type="radio"]';
const INNER_CONTAINER = ".universal-filters__inner";

const MOBILE_STATE_CLOSED = "closed";
const MOBILE_STATE_OPENED = "open";
const MOBILE_STATE_ADVANCED = "advanced";

const NAME_PRODUCT_TYPE = "product_type";
const NAME_CATEGORY = "category";
const NAME_SUBCATEGORY = "subcategory";

let appliedFilters = null;
let pendingFilters = new Set();

export default baustein.register("universal-filters", {
    tagName: "form",

    template,

    defaultOptions: {
        queryString: null,
        dynamicLoad: null,
        feedUrl: null,
        lazyLoad: false,
    },

    setupEvents(add) {
        add("change", this._onChange);
        add("closeFilters", "mobile-filters-toolbar", this._onCloseFilters);
        add("backFilters", "mobile-filters-toolbar", this._onBackFilters);
        add("applyFilters", "mobile-filters-toolbar", this._onApplyFilters);
        add("clearFilters", "mobile-filters-toolbar", this._onClearFilters);
        add("click", RADIO_BUTTONS_SELECTOR, this._onRadioButtonClick);
        add("click", CLEAR_BUTTON_SELECTOR, this._onClearButtonClick);
        add("click", MOBILE_SECTION_BUTTON_SELECTOR, this._onMobileSectionButtonClick);
    },

    getInitialRenderContext() {
        return {
            universal_filters: {
                mobile_filters_toolbar: {},
            },
        };
    },

    init() {
        this.client = new rothko.Rothko("modules/universal_filters");
        this.client.on("data", this._onGetData.bind(this));
        this.mobileState = MOBILE_STATE_CLOSED;
        // Initialise applied filters if we're not lazy loading them
        if (!this.options.lazyLoad) {
            appliedFilters = this.getSerializedForm();
            store.dispatch(setActiveFilters(appliedFilters));
        }

        function activeFilterStateSelector(state) {
            return state.feedFiltersReducer.active;
        }

        let firstLoad = this.options.lazyLoad;
        this.unsubscribeToStateWatcher = watchState(activeFilterStateSelector, (updatedFilters) => {
            // If we've been lazy loaded, we want to update Redux but not
            //  trigger a history update when the filter data arrive
            if (firstLoad && _.isEqual(updatedFilters, appliedFilters)) {
                firstLoad = false;
                return;
            }
            // Race condition - if we're in a lazy loaded feed (eg search) and
            //  the user changes the sorting before the filter data loads then
            //  everything breaks.
            if (
                firstLoad &&
                Object.values(updatedFilters).length == 1 &&
                updatedFilters["view"] != null
            ) {
                let params = new URLSearchParams(window.location.search);
                params.set("view", updatedFilters["view"]);
                updatedFilters = params.toJSON();
                // If the user now changes filter settings, the sorting will
                // reset but the filter menu will not, which is a very confusing
                // experience (but how things currently work)
            }

            appliedFilters = updatedFilters;
            history.updateQuery(updatedFilters, false);
            analytics.pageView();
            // Also emit the existing event as there is too much to untangle in one PR ...
            this.emit("applyFilters", { ...updatedFilters });
            // Close feed sidebar
            store.dispatch(closeSidebar());
            analytics.event("filter", "close");
        });
    },

    onInsert() {
        this._removeVisiblityWatcher = addVisibilityWatcher(this.el, (visible) => {
            if (visible) {
                this._fetchRenderData();
                this._removeVisiblityWatcher();
            }
        });
    },

    onRemove() {
        if (this._removeVisiblityWatcher) {
            this._removeVisiblityWatcher();
        }
        this.unsubscribeToStateWatcher();
    },

    /**
     * Sends analytics event based on what has been changed
     *
     * @param {Event} event
     * @returns {Promise}
     * @private
     */
    _onChangeAnalytics(event) {
        // send analytics event
        const el = event.target;

        // by default assume if this input's 'checked' property is true then a filter is being added
        let addingFilter = !!el.checked;
        let analyticsLabel = el.name;

        // handle clearing a choice
        if (el.hasAttribute("data-clear-name")) {
            addingFilter = false;
            analyticsLabel = el.getAttribute("data-clear-name");
        }
        // handle special cases
        switch (analyticsLabel) {
            // get the analytics label from the gender
            case "gender":
                // Converts the value 'Men' to '_men'.
                // Note 'Men' is the input value, not the label.
                // The label shown to the user will be translated.
                analyticsLabel += `_${el.value.toLowerCase()}`;
                break;

            // convert final price to / from to a general price range event
            case "final_price_to":
            case "final_price_from":
                addingFilter = true;
                analyticsLabel = "price_range";
                break;
        }

        return analytics.event(
            "FILTER",
            addingFilter ? "ADD_FILTER" : "REMOVE_FILTER",
            analyticsLabel
        );
    },

    /**
     * Called every time a filter input element is changed.
     * Updates the filters and accordions.
     *
     * @param {Event} event
     * @private
     */
    _onChange(event) {
        // ignore text input changes
        if (traversal.matches(event.target, "input[type=text]")) {
            return;
        }

        const analyticsPromise = this._onChangeAnalytics(event);
        const name = event.target.getAttribute("name");

        // clear inputs for product type and category
        if (name === NAME_PRODUCT_TYPE || name === NAME_CATEGORY) {
            this._clearInputs(name, event.target);
        }

        // mobile and tablet must apply the filters directly but still toggle the accordion
        if (browser.getViewport() < browser.VIEWPORTS.DESKTOP) {
            this._setPendingFilter(name);
            this._toggleClosestAccordion(event.target, name);

            // update filters if we're not using advanced options
            if (this.mobileState === MOBILE_STATE_OPENED) {
                this._fetchRenderData(this.getSerializedForm());
            }
            return;
        }

        // apply filters when analytics have resolved
        analyticsPromise.then(() => {
            this._applyFilters(event);
        });
    },

    /**
     * Clears the checked value of a group of inputs and their children. Also
     * toggles the closest accordion to those inputs which collapses them.
     *
     * @param {Event} event
     * @param {Element} link
     * @private
     */
    _onClearButtonClick(event, link) {
        const analyticsPromise = this._onChangeAnalytics(event);
        const inputName = link.getAttribute("data-clear-name");
        const inputs = this._clearInputs(inputName);

        // toggle the closest accordions to the inputs
        if (browser.getViewport() < browser.VIEWPORTS.DESKTOP) {
            this._setPendingFilter(inputName);
            this._toggleClosestAccordions(inputs, inputName);
            return;
        }

        // apply filters when analytics have resolved
        analyticsPromise.then(() => {
            this._applyFilters(event);
        });
    },

    /**
     * Un-check a checked radio button and trigger a change in filters.
     *
     * @param {Event} event
     * @private
     */
    _onRadioButtonClick(event) {
        const input = event.target;

        // defaultChecked will be true if it was set to true before, meaning we turn it off
        if (input.checked === input.defaultChecked) {
            input.checked = false;
            const inputName = input.getAttribute("name");
            this._toggleClosestAccordion(input, inputName);
            this._onChange(event);
        }
    },

    /**
     * When the user clicks on to a mobile section button (ie. Colors, Designers, etc), it should
     * expand that section to take up the full screen.
     * @param {Event} event - the event object from the click
     * @param {Element} link - the link/button that the user tapped
     * @private
     */
    _onMobileSectionButtonClick(event, link) {
        this.mobileState = MOBILE_STATE_ADVANCED;

        const id = link.getAttribute("data-href");
        let activeFilter = null;

        // hide all filter sections that are not the active filter section
        for (let filter of this.el.querySelectorAll(".universal-filters__filter-section")) {
            if (filter.getAttribute("data-universal-filter-section-id") === id) {
                activeFilter = filter;
            } else {
                filter.classList.add("hidden");
            }
        }

        // open the selected filter's accordion
        const activeFilterAccordion = activeFilter.querySelector("[data-accordion-toggle]");
        this._setAccordion(activeFilterAccordion);

        // show back button and hide cancel button
        const mobileFiltersComponent = this.findComponent("mobile-filters-toolbar");
        this.invoke(mobileFiltersComponent, "toggleBackButton", true);

        // show alternative mobile header
        this._toggleMobileHeaders(activeFilter);

        // hide flat button
        this._toggleFilterFlatButton(activeFilter);

        // get inner container
        const innerContainer = this.el.querySelector(INNER_CONTAINER);

        // scroll to the top
        innerContainer.scrollTop = 0;
    },

    _resetToParentFilterView() {
        const activeFilter = this.el.querySelector(
            ".universal-filters__filter-section--accordion:not(.hidden)"
        );

        this._toggleMobileHeaders(activeFilter);
        this._toggleFilterFlatButton(activeFilter);

        // un-hide default the filter sections
        const elementsToShow = Array.from(
            this.el.querySelectorAll(".universal-filters__filter-section")
        );
        elementsToShow.forEach((element) => {
            element.classList.remove("hidden");
        });

        // collapses all accordions in the filters
        this._collapseAllAccordions();

        // hide back button and un-hide cancel button
        const mobileFiltersComponent = this.findComponent("mobile-filters-toolbar");
        this.invoke(mobileFiltersComponent, "toggleBackButton", false);

        // reset margin-top to default
        style.setStyle(this.el.querySelector(INNER_CONTAINER), "margin-top", null);
    },

    _onApplyFilters(event) {
        event.stopPropagation();

        const subFilterActive = this.el.querySelector(
            ".universal-filters__filter-section-title-nested:not(.hidden)"
        );

        // reset filter to default view
        if (subFilterActive) {
            this._resetToParentFilterView();
            this._onCloseFilters();
        }

        this._applyFilters(event);
        analytics.event("FILTER", "APPLY");
    },

    /**
     * Updates the universal filters based on the filters selected on the page. Form submit
     * is used unless 'dynamicLoad' is enabled then the products are dynamically updated.
     *
     * @param {Event} event
     * @private
     */
    _applyFilters(event) {
        if (!this.options.dynamicLoad) {
            event.preventDefault();
            this.el.submit();
            return;
        }

        const filters = this.getSerializedForm();
        // set pending and applied filters to be equal
        appliedFilters = filters;
        store.dispatch(setActiveFilters(appliedFilters));
        store.dispatch(clearPendingFilters());
        pendingFilters.clear();
        this._updateMobileToolbarApplyButtonDetail();
    },

    /**
     * Called when back button is triggered on mobile filters
     *
     * @private
     */
    _onBackFilters() {
        this.mobileState = MOBILE_STATE_OPENED;

        this._fetchRenderData(this.getSerializedForm());
        this._resetToParentFilterView();
    },

    /**
     * Called when clear button is triggered on mobile filters
     *
     * @private
     */
    _onClearFilters() {
        analytics.event("FILTER", "CLEAR");

        // reset filters
        appliedFilters = null;
        store.dispatch(clearFilters());
        pendingFilters.clear();
        this._fetchRenderData({});

        // reload feed with no filters applied
        this.emit("applyFilters", {
            filters: {},
        });
    },

    /**
     * Called when mobile filters are opened
     */
    onOpenFilters() {
        this.mobileState = MOBILE_STATE_OPENED;

        const renderContext = this.getRenderContext();
        if (
            renderContext.universal_filters.filter_section_list &&
            renderContext.universal_filters.filter_section_list.length
        ) {
            // collapses all accordions in the filters
            this._collapseAllAccordions();
        }
    },

    /**
     * Called when mobile filters are closed
     */
    _onCloseFilters() {
        this.mobileState = MOBILE_STATE_CLOSED;
        store.dispatch(closeSidebar());
        analytics.event("filter", "close");
    },

    /**
     * Toggles mobile header and nested mobile header
     *
     * @param {Element} el - Filter element to query select from
     * @private
     */
    _toggleMobileHeaders(el) {
        // hide main mobile header
        const mobileHeader = el.querySelector(".universal-filters__filter-section-title-main");
        if (mobileHeader) {
            mobileHeader.classList.toggle("hidden");
        }

        // show nested mobile header
        const nestedMobileHeader = el.querySelector(
            ".universal-filters__filter-section-title-nested"
        );
        if (nestedMobileHeader) {
            nestedMobileHeader.classList.toggle("hidden");
        }
    },

    /**
     * Toggles hiding and showing the mobile section buttons
     *
     * @param {Element} el - Filter element to query select from
     * @private
     */
    _toggleFilterFlatButton(el) {
        const filterGroupTitle = el.querySelector(MOBILE_SECTION_BUTTON_SELECTOR);
        if (filterGroupTitle) {
            filterGroupTitle.classList.toggle("hidden");
        }
    },

    /**
     * Collapses all the accordions in filters
     *
     * @private
     */
    _collapseAllAccordions() {
        if (browser.getViewport() < browser.VIEWPORTS.DESKTOP) {
            const accordionContainer = this.findComponent("accordion-container");
            // only collapse top-level accordions, so active category choice still shows its subcategories
            const accordionsToCollapseQuery =
                ".universal-filters__filter-section > [data-accordion-toggle]";
            const accordionsToCollapse = Array.from(
                accordionContainer.el.querySelectorAll(accordionsToCollapseQuery)
            );
            accordionContainer.collapseAccordions(accordionsToCollapse);
        }
    },

    /**
     * Toggles the collapse and expanded state of the closest accordion to an element.
     *
     * @param {Element} element
     * @param {String} name - name of the input
     * @private
     */
    _toggleClosestAccordion(element, name) {
        // Only expand product type and category accordions
        if (name === NAME_PRODUCT_TYPE || name === NAME_CATEGORY) {
            const accordion = traversal.closest(element, "[data-accordion-toggle]");
            this._setAccordion(accordion);
        }
    },

    /**
     * Sets the current accordion on the accordion container.
     *
     * @param {Element} accordion
     * @private
     */
    _setAccordion(accordion) {
        const accordionContainer = this.findComponent("accordion-container");
        if (accordionContainer && accordion) {
            accordionContainer.setCurrentAccordion(accordion);
        }
    },

    /**
     * Toggles the closest accordions to the inputs.
     *
     * @param {Array} inputs - array of input elements
     * @param {String} inputName
     * @private
     */
    _toggleClosestAccordions(inputs, inputName) {
        // toggle accordion on mobile and tablet
        if (browser.getViewport() < browser.VIEWPORTS.DESKTOP) {
            inputs.forEach((input) => {
                this._toggleClosestAccordion(input, inputName);
            });
        }
    },

    /**
     * Clears a set of inputs based on their input name.
     *
     * @param {String} inputName - name of the input
     * @param {Element} inputExcluded - input element to exclude from the clear
     * @returns {Array} inputs - Array of input elements
     * @private
     */
    _clearInputs(inputName, inputExcluded = null) {
        let inputNames = inputName.split(",");

        // Add category and/or subcategory
        if (inputName === NAME_PRODUCT_TYPE) {
            inputNames.push(NAME_CATEGORY);
            inputNames.push(NAME_SUBCATEGORY);
        } else if (inputName === NAME_CATEGORY) {
            inputNames.push(NAME_SUBCATEGORY);
        }

        // Create queries
        const queries = inputNames.map((key) => {
            return `input[name="${key}"]:checked`;
        });

        const query = queries.join(",");
        const inputs = Array.from(this.el.querySelectorAll(query));
        inputs.forEach((input) => {
            if (inputExcluded && inputExcluded === input) {
                return;
            }
            input.checked = false;
        });

        return inputs;
    },

    /**
     * Fetches the render context using rothko and updates it. Returns a promise that will be
     * resolved when the render context has been updated.
     * @param {Object} filters - optional, otherwise gets filters from query string
     * @returns {Promise}
     * @private
     */
    _fetchRenderData(filters = null) {
        if (!this.options.lazyLoad) {
            this.options.lazyLoad = true;
            return Promise.resolve();
        }

        let query;
        if (!filters) {
            query = new url.URLSearchParams(this.options.queryString);
        } else {
            query = url.toURLSearchParams(filters);
        }
        query.set("feed_url", this.options.feedUrl);

        return this.client.fetch(query);
    },

    _onGetData(ev, data) {
        let scrollPos;

        if (this.isDestroyed()) {
            return;
        }

        // don't render if in an advanced mobile filter
        if (this.mobileState === MOBILE_STATE_ADVANCED) {
            return;
        } else if (this.mobileState === MOBILE_STATE_OPENED) {
            // get inner container's scroll position
            const innerContainer = this.el.querySelector(INNER_CONTAINER);
            scrollPos = innerContainer ? innerContainer.scrollTop : 0;
        }

        let context = data;

        this.setRenderContext({
            universal_filters: context,
        });

        // restore scroll position
        if (scrollPos) {
            this.el.querySelector(INNER_CONTAINER).scrollTop = scrollPos;
        }

        let performLazyLoadHack = this.options.lazyLoad && appliedFilters == null;
        appliedFilters = this.getSerializedForm();
        if (performLazyLoadHack) {
            store.dispatch(setActiveFilters(appliedFilters));
        }

        store.dispatch(hideSeoContent());

        // Fix things a re-render might have overridden
        this._collapseAllAccordions();
        this._updateMobileToolbarApplyButtonDetail();
    },

    onOptionsChange() {
        this._persistFilters = this._getFiltersToPersist();
        this._fetchRenderData();
    },

    _getFiltersToPersist() {
        const accordionContainer = this.findComponent("accordion-container");

        if (!accordionContainer) {
            return null;
        }

        let expandedFilters = accordionContainer.getExpandedAccordions();
        expandedFilters = expandedFilters.filter((name) => {
            const element = this.el.querySelector(`#${name}`);
            const parent = traversal.closest(
                element,
                '[data-universal-filter-section-id="section-category"]'
            );
            return !parent;
        });
        return expandedFilters;
    },

    /**
     * Override the setRenderContext method because some of the subcomponents need a bit of
     * niceness applied to get their expected behaviour. This will pull out their specific
     * bits of info, and supply it later.
     * @param context
     */
    setRenderContext(context) {
        if (!context.universal_filters) {
            return;
        }

        // record scroll positions of options elements, so we can restore this later
        let scrollableAreaPositions = this.findComponents("scrollable-area").map((scrollableArea) =>
            scrollableArea.getPosition()
        );

        // deep clone plz
        let data = JSON.parse(JSON.stringify(context.universal_filters));

        // retailers and designers have special limits, so we get their own components to
        // handle these. We've exposed a special function call so that we can supply them
        // with their data, rather than rendering directly via the context. First, however,
        // we need to strip this data out from the data.
        let retailerOptions;
        let designerOptions;
        for (let filterSectionIndex in data.filter_section_list) {
            if (!data.filter_section_list.hasOwnProperty(filterSectionIndex)) {
                continue;
            }

            const filterSection = data.filter_section_list[filterSectionIndex];

            if (retailerOptions && designerOptions) {
                break;
            }

            if (!filterSection.option_groups.length) {
                continue;
            }

            if (filterSection.id === "section-designer-slug") {
                designerOptions = filterSection.option_groups[0].options;
                filterSection.option_groups[0].options = [];
                continue;
            }

            if (filterSection.id === "section-retailer-slug") {
                retailerOptions = filterSection.option_groups[0].options;
                filterSection.option_groups[0].options = [];
            }
        }

        baustein.Component.prototype.setRenderContext.call(this, {
            universal_filters: data,
        });

        // now that things have been re-rendered, we find these children and give them
        // the data they're waiting for
        const designerGroup = baustein.fromElement(
            this.el.querySelector("#universal-filter-option-group-designer-slug")
        );
        const retailerGroup = baustein.fromElement(
            this.el.querySelector("#universal-filter-option-group-retailer-slug")
        );

        if (designerGroup) {
            designerGroup.updateOptions({
                options: designerOptions,
            });
            designerGroup.setFilterData(designerOptions);
        }
        if (retailerGroup) {
            retailerGroup.updateOptions({
                options: retailerOptions,
            });
            retailerGroup.setFilterData(retailerOptions);
        }

        // restore scroll positions of options
        if (scrollableAreaPositions.length) {
            this.findComponents("scrollable-area").forEach((scrollableArea, i) => {
                scrollableArea.setPosition(scrollableAreaPositions[i]);
            });
        }

        if (this._persistFilters) {
            this.findComponent("accordion-container").expandAccordions(this._persistFilters);
        }
    },

    /**
     * Returns the form as a serialized object.
     *
     * @returns {Object} serialized form
     */
    getSerializedForm() {
        return form.serialize(this.el);
    },

    /**
     * Figure out if a filter has changed since the last apply, add it to a list if so
     * @param inputName {string} - name of the filter which the control relates to
     */
    _setPendingFilter(inputName) {
        let inputNames = [inputName];

        if (inputName === NAME_PRODUCT_TYPE) {
            inputNames.push(NAME_CATEGORY);
            inputNames.push(NAME_SUBCATEGORY);
        } else if (inputName === NAME_CATEGORY) {
            inputNames.push(NAME_SUBCATEGORY);
        }

        inputNames.forEach((inputName) => {
            const pendingChanges = this.getSerializedForm()[inputName];
            const appliedChanges = appliedFilters[inputName];
            const pending = !_.isEqual(pendingChanges, appliedChanges);

            if (pending !== pendingFilters.has(inputName)) {
                if (pending) {
                    pendingFilters.add(inputName);
                } else {
                    pendingFilters.delete(inputName);
                }
            }
        });

        this._updateMobileToolbarApplyButtonDetail();
    },

    /**
     * Updates the detail text shown in the 'apply filters' button in the mobile toolbar
     */
    _updateMobileToolbarApplyButtonDetail() {
        let detail = pendingFilters.size;

        // Never show detail if there are no pending filters to be applied
        let pending = !!pendingFilters.size;
        detail = pending ? detail : null;

        const toolbarComponent = this.findComponent("mobile-filters-toolbar");
        this.invoke(toolbarComponent, "updateApplyButton", detail, pending);
    },
});
